import React, { useState, useEffect } from 'react';
import api from '../api'; 
import logoImg from '../assets/logo3.png';
import SiteSwitcher from './SiteSwitcher'; 
import { Bell, Menu, X, Sun, Moon } from 'lucide-react'; // <--- New Icons

// Shared Theme Colors (Currently Static)
export const THEME = {
    card: '#1e293b', border: '#334155', textSec: '#94a3b8', 
    primary: '#3b82f6', success: '#10b981', danger: '#ef4444', warning: '#facc15',
    bg: '#0f172a'
};

const SaaSLayout = ({ title, sidebar, children, onNavigate }) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    
    // Notifications State
    const [unreadCount, setUnreadCount] = useState(0);

    // Theme State (For future implementation of full light mode)
    const [isDarkMode, setIsDarkMode] = useState(true);

    // Handle Window Resize (Responsive Logic)
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (!mobile) setSidebarOpen(false); 
        };
        window.addEventListener('resize', handleResize);
        handleResize(); 
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch Unread Count
    useEffect(() => {
        const fetchNotifications = async () => {
            try {
                const res = await api.get('notifications/');
                const count = res.data.filter(n => !n.is_read).length;
                setUnreadCount(count);
            } catch (err) {
                console.error("Failed to fetch notifications");
            }
        };
        
        fetchNotifications();
        
        // Listen for "read" events from other components to update badge instantly
        const handleLocalRead = () => setUnreadCount(0);
        window.addEventListener('notificationsRead', handleLocalRead);

        const interval = setInterval(fetchNotifications, 60000);
        return () => {
            clearInterval(interval);
            window.removeEventListener('notificationsRead', handleLocalRead);
        };
    }, []);

    const toggleTheme = () => {
        setIsDarkMode(!isDarkMode);
        // FUTURE TODO: Save to localStorage and update a ThemeContext here
        // alert("Light mode logic would require refactoring global THEME constants.");
    };

    // Inline Scrollbar Styling
    const scrollStyle = {
        scrollbarWidth: 'thin', 
        scrollbarColor: '#334155 transparent' 
    };

    return (
        <div style={{
            display: 'flex', 
            width: '100vw', 
            height: '100vh', 
            background: THEME.bg, 
            color: 'white', 
            overflow: 'hidden' 
        }}>
            
            {/* --- MOBILE BACKDROP --- */}
            {isMobile && sidebarOpen && (
                <div 
                    onClick={() => setSidebarOpen(false)}
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0,0,0,0.5)', zIndex: 40
                    }}
                />
            )}

            {/* --- SIDEBAR --- */}
            <aside style={{
                width: '260px', 
                flexShrink: 0, 
                background: '#0f172a',
                borderRight: `1px solid ${THEME.border}`,
                display: 'flex', flexDirection: 'column',
                height: '100%',
                position: isMobile ? 'fixed' : 'relative',
                left: isMobile && !sidebarOpen ? '-260px' : '0', 
                zIndex: 50,
                transition: 'left 0.3s ease',
                boxShadow: isMobile && sidebarOpen ? '5px 0 15px rgba(0,0,0,0.5)' : 'none'
            }}>
                {/* Brand Header */}
                <div style={{
                    height: '60px', 
                    padding: '0 20px', 
                    borderBottom:`1px solid ${THEME.border}`, 
                    display:'flex', 
                    alignItems:'center', 
                    justifyContent:'space-between', 
                    flexShrink: 0
                }}>
                    <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
                        <img 
                            src={logoImg} 
                            alt="Logo" 
                            style={{height: '35px', width: 'auto', objectFit: 'contain'}} 
                        />
                        <div style={{fontSize:'1.3rem', fontWeight:'bold', letterSpacing: '0.5px'}}>
                            <span style={{color: 'white'}}>Site</span>
                            <span style={{color: '#3b82f6'}}>ACT</span>
                        </div>
                    </div>

                    {isMobile && (
                        <button onClick={() => setSidebarOpen(false)} style={{background:'transparent', border:'none', color:'white', cursor:'pointer'}}>
                            <X size={24} />
                        </button>
                    )}
                </div>

                {/* Navigation Items */}
                <div style={{
                    flex: 1, 
                    overflowY: 'auto', 
                    padding:'10px',
                    ...scrollStyle 
                }}>
                    {sidebar}
                </div>

                {/* User Footer */}
                <div style={{padding:'15px', borderTop:`1px solid ${THEME.border}`, background:'#020617', flexShrink: 0}}>
                    <div style={{fontSize:'0.8rem', color:THEME.textSec}}>Logged in as</div>
                    <div style={{fontWeight:'bold'}}>{localStorage.getItem('username') || 'User'}</div>
                </div>
            </aside>

            {/* --- MAIN CONTENT --- */}
            <main style={{
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column', 
                height: '100%', 
                overflow: 'hidden', 
                position: 'relative'
            }}>
                
                {/* Header Bar */}
                <header style={{
                    height: '60px', 
                    borderBottom: `1px solid ${THEME.border}`, 
                    display: 'flex', alignItems: 'center', 
                    padding: '0 20px', 
                    background: '#0f172a',
                    flexShrink: 0,
                    zIndex: 30
                }}>
                    <div style={{display:'flex', alignItems:'center', flex: 1}}>
                        {isMobile && (
                            <button 
                                onClick={() => setSidebarOpen(true)}
                                style={{
                                    background: 'transparent', border: 'none', color: 'white', 
                                    cursor: 'pointer', marginRight: '15px', display:'flex', alignItems:'center'
                                }}
                            >
                                <Menu size={24} />
                            </button>
                        )}
                        <h2 style={{margin:0, fontSize:'1.1rem', color:'white', fontWeight: 600}}>{title}</h2>
                    </div>

                    {/* --- RIGHT SIDE HEADER ITEMS --- */}
                    <div style={{display:'flex', alignItems:'center', gap:'20px'}}>
                        
                        {/* 1. SITE SWITCHER */}
                        <SiteSwitcher />

                        {/* 2. THEME TOGGLE (Visual Only for now) */}
                        <button 
                            onClick={toggleTheme}
                            style={{background:'transparent', border:'none', cursor:'pointer', color: THEME.textSec, display:'flex', alignItems:'center'}}
                            title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
                        >
                            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
                        </button>

                        {/* 3. NOTIFICATION BELL */}
                        <div 
                            onClick={() => onNavigate && onNavigate('profile')} 
                            style={{ position: 'relative', cursor: 'pointer', display:'flex', alignItems:'center' }}
                            title="Notifications"
                        >
                            <Bell size={22} color={unreadCount > 0 ? 'white' : THEME.textSec} />
                            {unreadCount > 0 && (
                                <span style={{
                                    position: 'absolute', top: -5, right: -5, 
                                    background: THEME.danger, color: 'white', 
                                    borderRadius: '50%', fontSize: '0.65rem', fontWeight: 'bold',
                                    width: '16px', height: '16px', 
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    border: '2px solid #0f172a' 
                                }}>
                                    {unreadCount > 9 ? '9+' : unreadCount}
                                </span>
                            )}
                        </div>
                    </div>

                </header>

                {/* Scrollable View Area */}
                <div style={{
                    flex: 1, 
                    overflowY: 'auto', 
                    padding: '20px', 
                    background: '#1e293b',
                    ...scrollStyle
                }}>
                    <div style={{maxWidth: '1200px', margin: '0 auto', paddingBottom: '40px'}}>
                        {children}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default SaaSLayout;